/**
 * Position Type Definitions
 *
 * TypeScript types matching backend Pydantic models for option positions,
 * PnL calculations, Greeks, market data, and dashboard metrics.
 */

// ============================================================================
// Enums
// ============================================================================

/**
 * Option type: call or put
 */
export const OptionType = {
  CALL: 'call',
  PUT: 'put',
} as const

export type OptionType = (typeof OptionType)[keyof typeof OptionType]

/**
 * Position side: long or short
 */
export const PositionSide = {
  LONG: 'long',
  SHORT: 'short',
} as const

export type PositionSide = (typeof PositionSide)[keyof typeof PositionSide]

/**
 * Position status: open or closed
 */
export const PositionStatus = {
  OPEN: 'open',
  CLOSED: 'closed',
} as const

export type PositionStatus = (typeof PositionStatus)[keyof typeof PositionStatus]

/**
 * Reason position was closed
 */
export const CloseReason = {
  MANUAL: 'manual',
  EXPIRED_WORTHLESS: 'expired_worthless',
  EXPIRED_ITM: 'expired_itm',
  ASSIGNED: 'assigned',
  EXERCISED: 'exercised',
} as const

export type CloseReason = (typeof CloseReason)[keyof typeof CloseReason]

// ============================================================================
// Base Position Types
// ============================================================================

/**
 * Base position fields (common to all position types)
 */
export interface PositionBase {
  ticker: string
  option_type: OptionType
  strike: number
  expiry: string // ISO date string (YYYY-MM-DD)
  quantity: number
  side: PositionSide
  premium: number // Per-share quote unless create/update explicitly declares another input unit
  premium_unit?: 'per_share' | 'per_contract' | 'total_position' // Explicit input units; API output is per_share
  contract_multiplier?: number | null // Quote-to-contract multiplier; legacy standard positions use 100
  entry_date: string // ISO date string (YYYY-MM-DD)
  notes?: string | null
  tags?: string[] | null
  profit_target?: number | null
  stop_loss?: number | null
  collateral?: number | null // User-provided collateral for undefined-risk strategies
}

/**
 * Position data for creating a new position (API request)
 * Includes all base position fields required to create a new position
 */
export interface PositionCreate extends PositionBase {
  portfolio_id?: string | null // Optional - defaults to user's default portfolio
  coverage_mode?: 'existing_shares' | 'new_position' | null
  covered_shares?: number | null
}

/** User-supplied provenance for a manual stock or option quote. */
export interface PriceObservation {
  date: string // Explicit price observation date (YYYY-MM-DD)
  source: string
  as_of?: string | null // Only an explicitly supplied observation instant
}

/**
 * Position data for updating an existing position (API request)
 * All fields are optional
 */
export interface PositionUpdate {
  ticker?: string
  option_type?: OptionType
  strike?: number
  expiry?: string
  quantity?: number
  side?: PositionSide
  premium?: number
  premium_unit?: 'per_share' | 'per_contract' | 'total_position'
  entry_date?: string
  notes?: string | null
  tags?: string[] | null
  profit_target?: number | null
  stop_loss?: number | null
  status?: PositionStatus
  exit_premium?: number
  exit_date?: string
  current_premium?: number | null
  price_observation?: PriceObservation
  underlying_price?: number | null
}

/**
 * Position data for closing a position (API request)
 */
export interface PositionClose {
  exit_premium: number
  exit_date: string // ISO date string (YYYY-MM-DD)
  quantity?: number // Optional contracts to close (defaults to full position)
  pmcc_short_call_position_id?: string | null
  pmcc_short_exit_premium?: number | null
}

/**
 * Complete position model (from database/API response)
 */
export interface Position extends PositionBase {
  premium_unit?: 'per_share' // Canonical API output; older responses may omit it
  deliverable_shares?: number | null
  is_adjusted_contract?: boolean | null
  id: string // UUID
  user_id: string // UUID
  portfolio_id: string // UUID - Portfolio this position belongs to
  strategy_id?: string | null // UUID - Strategy this position belongs to (if multi-leg)
  status: PositionStatus
  exit_premium?: number | null // Per-share option quote
  exit_date?: string | null // ISO date string (YYYY-MM-DD)
  close_timestamp?: string | null // ISO datetime string
  close_reason?: CloseReason | null // Reason position was closed
  created_at: string // ISO datetime string
  updated_at: string // ISO datetime string
  current_premium?: number | null // Current per-share option quote
  price_updated_at?: string | null // ISO datetime string
  price_observation?: PriceObservation | null
  // Per-contract Greeks (from database - updated by MarketDataUpdater)
  delta?: number | null
  gamma?: number | null
  theta?: number | null
  vega?: number | null
  rho?: number | null
  implied_volatility?: number | null
  is_greeks_estimated?: boolean
  underlying_price?: number | null // Current underlying stock price
}

/**
 * Underlying stock position (from assignment/exercise or manual entry)
 */
export interface UnderlyingPosition {
  id: string // UUID
  user_id: string // UUID
  portfolio_id?: string | null // UUID - Portfolio this position belongs to
  ticker: string
  quantity: number // Can be negative for short positions
  side: 'long' | 'short'
  entry_price: number
  entry_date: string // ISO date string
  entry_source: 'manual' | 'assignment' | 'exercise' | 'imported'
  status: 'open' | 'closed'
  strategy_id?: string | null // UUID - Strategy this position belongs to (if multi-leg)
  exit_price?: number | null
  exit_date?: string | null
  exit_reason?: 'manual' | 'assignment' | 'exercise' | 'imported' | null
  related_option_id?: string | null // Link to option position
  realized_pnl?: number | null // Cumulative realized P&L from partial sells
  notes?: string | null
  tags?: string[] | null
  created_at: string // ISO datetime string
  updated_at: string // ISO datetime string
}

/**
 * Underlying position with P&L calculations
 */
export interface UnderlyingPositionWithPnL extends UnderlyingPosition {
  pnl: number
  pnl_type: 'unrealized' | 'realized'
  roi: number
  capital_at_risk: number
  current_price?: number | null
  days_held: number
  realized_pnl?: number | null
}

/**
 * Stock trade action type
 */
export type StockTradeAction = 'buy' | 'sell'

/**
 * Individual buy/sell trade for a stock position
 */
export interface StockTrade {
  id: string // UUID
  user_id: string // UUID
  underlying_position_id: string // UUID
  action: StockTradeAction
  shares: number
  price: number
  trade_date: string // ISO date string
  cost_basis_at_trade?: number | null
  realized_pnl?: number | null
  notes?: string | null
  created_at: string // ISO datetime string
}

/**
 * Stock trade with parent position metadata for portfolio-level history.
 */
export interface StockTradeWithPosition extends StockTrade {
  ticker: string
  side: 'long' | 'short'
  position_entry_price: number
  position_entry_date: string
  position_status: 'open' | 'closed'
  portfolio_id?: string | null
}

/**
 * Request body for creating a stock trade
 */
export interface StockTradeCreate {
  action: StockTradeAction
  shares: number
  price: number
  trade_date: string // ISO date string (YYYY-MM-DD)
  notes?: string | null
}

/**
 * Response from recording a stock trade
 */
export interface StockTradeResponse {
  trade: StockTrade
  position_quantity: number
  position_entry_price: number
  position_realized_pnl: number
  position_status: string
}

// ============================================================================
// Enhanced Position Types (with metrics)
// ============================================================================

/**
 * Position with basic PnL calculations
 */
export interface PositionWithPnL extends Position {
  unrealized_pnl?: number | null
  realized_pnl?: number | null
  total_pnl: number
  pnl_percentage: number
  days_held: number
  days_to_expiry?: number | null
}

/**
 * Option Greeks for a position
 */
export interface PositionGreeks {
  position_delta: number
  position_gamma: number
  position_theta: number
  position_vega: number
}

/**
 * Comprehensive position metrics (PnL, ROI, Greeks)
 */
export interface PositionMetrics {
  // PnL Metrics
  pnl: number
  pnl_type: 'unrealized' | 'realized'
  roi: number
  capital_at_risk: number
  days_to_expiration?: number | null

  // Greeks (optional)
  position_delta?: number | null
  position_gamma?: number | null
  position_theta?: number | null
  position_vega?: number | null

  // Market Data (optional)
  current_premium?: number | null
  underlying_price?: number | null
}

/**
 * Enhanced position with all metrics: PnL, ROI, Greeks, and market data
 * This is the unified response model from GET /api/positions
 */
export interface PositionEnhanced extends Position {
  // PnL Metrics
  pnl: number
  pnl_type: 'unrealized' | 'realized'
  roi: number
  capital_at_risk: number
  days_to_expiration?: number | null

  // Greeks (optional - may not be available for all positions)
  position_delta?: number | null
  position_gamma?: number | null
  position_theta?: number | null
  position_vega?: number | null

  // Market Data (optional)
  current_premium?: number | null
  underlying_price?: number | null

  // Additional computed fields
  days_held?: number | null
}

// ============================================================================
// List Response Types
// ============================================================================

/**
 * Portfolio-level metrics (aggregated from positions)
 */
export interface PortfolioMetrics {
  // PnL aggregates
  total_pnl: number
  total_unrealized: number
  total_realized: number
  average_pnl: number

  // Greeks aggregates
  portfolio_delta: number
  portfolio_theta: number
  portfolio_vega: number
}

/**
 * Response model for list of positions (basic)
 */
export interface PositionList {
  positions: Position[]
  total: number
  open_count: number
  closed_count: number
}

/**
 * Response model for list of enhanced positions (with metrics)
 */
export interface PositionEnhancedList {
  positions: PositionEnhanced[]
  total: number
  open_count: number
  closed_count: number
  portfolio_metrics?: PortfolioMetrics | null
}

/**
 * Trade highlight type - can be a simple number (legacy) or detailed object
 */
export type TradeHighlight =
  | number
  | {
      pnl: number
      ticker: string
      strike: number
      option_type: string
      side: string
      quantity: number
      expiry: string | null
    }

/**
 * Position statistics and summary
 */
export interface PositionStats {
  total_positions: number
  open_positions: number
  closed_positions: number
  total_pnl: number
  unrealized_pnl: number
  realized_pnl: number
  win_rate: number
  average_pnl: number
  best_trade?: TradeHighlight | null
  worst_trade?: TradeHighlight | null
}

// ============================================================================
// Dashboard Metrics
// ============================================================================

/**
 * Comprehensive dashboard metrics
 * Response from GET /api/dashboard/metrics
 */
export interface DashboardMetrics {
  // Position counts
  total_positions: number
  open_positions: number
  closed_positions: number

  // PnL metrics
  total_pnl: number
  unrealized_pnl: number
  realized_pnl: number
  total_capital_at_risk: number
  option_capital_at_risk?: number
  stock_capital_at_risk?: number
  combined_capital_at_risk?: number
  has_undefined_risk_without_collateral?: boolean
  capital_is_complete?: boolean
  unresolved_trade_group_count?: number
  average_roi: number

  // Performance metrics
  win_rate: number
  best_trade?: TradeHighlight | null
  worst_trade?: TradeHighlight | null

  // Portfolio Greeks
  portfolio_delta: number
  portfolio_gamma: number
  portfolio_theta: number
  portfolio_vega: number

  // Additional metrics
  days_to_expiry_avg?: number | null
  cash_balance?: number
  stock_value?: number
  option_value?: number
  total_portfolio_value?: number
  allocation?: {
    stocks_pct: number
    options_pct: number
    cash_pct: number
  }
  valuation?: PortfolioValuation
  /** Gross option values from the same position snapshot as P&L and valuation. */
  option_exposure?: Array<{ ticker: string; value: number }>
  /** Gross stock values from the same position snapshot as portfolio valuation. */
  stock_exposure?: Array<{ ticker: string; value: number }>
  /** Stock P&L from the valuation snapshot, including partial-sale realized gains. */
  stock_pnl?: { unrealized: number; realized: number; total: number }
  stock_pnl_calculation_failure_count?: number
}

export interface PortfolioValuation {
  is_partial?: boolean
  isPartial?: boolean
  uncoveredInstruments?: string[]
  cash_value?: number
  stock_value?: number
  option_value?: number
  stock_gross_market_value?: number
  option_gross_market_value?: number
  stock_signed_market_value?: number
  option_signed_market_value?: number
  option_net_market_value?: number
  option_exposure?: number
  total_value?: number
  cashValue?: number
  stockValue?: number
  optionValue?: number
  stockGrossMarketValue?: number
  optionGrossMarketValue?: number
  stockSignedMarketValue?: number
  optionSignedMarketValue?: number
  optionExposure?: number
  totalValue?: number
  cash_balance?: number
  stock_market_value?: number
  option_market_value?: number
  stock_exposure?: number
  option_gross_value?: number
  total_portfolio_value?: number
  total_account_value?: number
  invested_value?: number
  investedValue?: number
  stocks_allocation_pct?: number
  options_allocation_pct?: number
  cash_allocation_pct?: number
  stocksAllocationPct?: number
  optionsAllocationPct?: number
  cashAllocationPct?: number
  allocation?: {
    stocks_pct?: number
    options_pct?: number
    cash_pct?: number
  }
  cash_currency?: string
  cashCurrency?: string
  cash_source_label?: string
  cashSourceLabel?: string
  cash_source_types?: string[]
  cash_last_updated?: string | null
  cashLastUpdated?: string | null
  cash_status?: string
  cashStatus?: string
  is_cash_reliable?: boolean
  isCashReliable?: boolean
  cash_reliability_reason?: string | null
  cashReliabilityReason?: string | null
}

// ============================================================================
// CSV Import/Export Types
// ============================================================================

/**
 * Broker type for CSV imports
 */
export type BrokerType = 'ibkr' | 'robinhood' | 'schwab' | 'fidelity' | 'generic'

/**
 * Parsed position from CSV (API response)
 */
export interface ParsedPosition {
  source_row?: number | null
  import_source?: 'positions_snapshot' | null
  ticker: string
  option_type: string
  strike: number
  expiry: string // ISO date string
  quantity: number
  side?: string | null
  premium: number
  entry_date: string // ISO date string
  status: string
  exit_premium?: number | null
  exit_date?: string | null
  close_reason?: string | null
  collateral?: number | null // User-provided collateral for undefined-risk strategies
}

/**
 * Validation warning for CSV import
 */
export interface ValidationWarning {
  row: number
  field: string
  message: string
}

/**
 * Represents a potential duplicate position found during import
 */
export interface DuplicatePosition {
  ticker: string
  option_type: string
  strike: number
  expiry: string
  entry_date: string
  existing_id: string
}

/**
 * Represents a potential duplicate stock position found during import
 */
export interface DuplicateStockPosition {
  ticker: string
  quantity: number
  side: string
  entry_price: number
  entry_date: string
  status: string
  existing_id: string
}

/**
 * Parsed stock position from CSV import
 */
export interface ParsedStockPosition {
  source_row?: number | null
  import_source?: 'positions_snapshot' | null
  currency?: string | null
  currency_conflict?: boolean
  ticker: string
  quantity: number
  side: string
  entry_price: number
  entry_date: string
  entry_source?: 'manual' | 'assignment' | 'exercise' | 'imported'
  status: string
  exit_price?: number | null
  exit_date?: string | null
  exit_reason?: 'manual' | 'assignment' | 'exercise' | 'imported' | null
  realized_pnl?: number | null
  position_type: string
}

export interface ParsedCashTransaction {
  transaction_date: string
  transaction_type: string
  amount: number
  currency?: string
  description?: string | null
  source?: string
  is_balance_snapshot?: boolean
  raw_hash?: string | null
  position_type: string
}

/**
 * Response from POST /api/csv/import (parse CSV)
 */
export interface CSVImportResponse {
  success: boolean
  message: string
  broker?: string | null
  positions_count: number
  positions: ParsedPosition[]
  stock_positions?: ParsedStockPosition[]
  stock_positions_count?: number
  cash_transactions?: ParsedCashTransaction[]
  cash_transactions_count?: number
  source_rows_count?: number
  skipped_rows_count?: number
  failed_rows_count?: number
  errors: string[]
  warnings?: ValidationWarning[]
  potential_duplicates?: DuplicatePosition[]
  potential_stock_duplicates?: DuplicateStockPosition[]
  duplicate_count?: number
  option_duplicate_count?: number
  stock_duplicate_count?: number
  pmcc_detected_count?: number
  pmcc_detected_underlyings?: string[]
  pmcc_ambiguous_count?: number
}

/**
 * Request for POST /api/csv/confirm-import
 */
export interface ConfirmImportRequest {
  positions: ParsedPosition[]
  stock_positions?: ParsedStockPosition[]
  cash_transactions?: ParsedCashTransaction[]
  portfolio_id?: string // Target portfolio (defaults to user's default portfolio)
}

/**
 * Response from POST /api/csv/confirm-import
 */
export interface ConfirmImportResponse {
  success: boolean
  message: string
  imported_count: number
  failed_count: number
  open_count: number
  closed_count: number
  expired_count: number
  errors: string[]
  // Stock import stats
  stock_imported_count?: number
  stock_failed_count?: number
  stock_open_count?: number
  stock_closed_count?: number
  cash_ledger_entries_created?: number
  cash_imported_count?: number
  cash_duplicate_skipped_count?: number
  // Duplicate skip stats (idempotent incremental imports)
  duplicate_skipped_count?: number
  option_duplicate_skipped_count?: number
  stock_duplicate_skipped_count?: number
  pmcc_strategies_created?: number
  pmcc_short_calls_attached?: number
  diagonal_spreads_created?: number
  pmcc_ambiguous_count?: number
  evidence_imported_count?: number
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * API error response
 */
export interface APIError {
  detail?: unknown
  error?: string
}

/**
 * API success response (generic)
 */
export interface APISuccessResponse {
  success: boolean
  message: string
}

/**
 * Asset filter for dashboard
 * Controls which asset types are shown in dashboard metrics
 */
export const AssetFilter = {
  ALL: 'all',
  OPTIONS: 'options',
  STOCKS: 'stocks',
} as const

export type AssetFilter = (typeof AssetFilter)[keyof typeof AssetFilter]

/**
 * Sort direction for tables
 */
export type SortDirection = 'asc' | 'desc'

/**
 * Sort field for positions table
 */
export type PositionSortField =
  | 'ticker'
  | 'expiry'
  | 'entry_date'
  | 'pnl'
  | 'roi'
  | 'days_to_expiration'
  | 'status'

/**
 * Filter criteria for positions
 */
export interface PositionFilters {
  status?: PositionStatus
  ticker?: string
  option_type?: OptionType
  min_pnl?: number
  max_pnl?: number
  min_expiry?: string
  max_expiry?: string
}

/**
 * Chart data point for PnL history
 */
export interface PnLDataPoint {
  date: string // ISO date string
  pnl: number
  cumulative_pnl: number
}

/**
 * Exposure data for charts
 */
export interface ExposureData {
  ticker: string
  capital_at_risk: number
  percentage: number
  position_count: number
}

// ============================================================================
// Cash Ledger Types
// ============================================================================

/**
 * Transaction types for cash ledger
 */
export const TransactionType = {
  PREMIUM_PAID: 'premium_paid',
  PREMIUM_RECEIVED: 'premium_received',
  STOCK_PURCHASE: 'stock_purchase',
  STOCK_SALE: 'stock_sale',
  EQUITY_BUY: 'equity_buy',
  EQUITY_SELL: 'equity_sell',
  OPTION_BUY_TO_OPEN: 'option_buy_to_open',
  OPTION_SELL_TO_OPEN: 'option_sell_to_open',
  OPTION_BUY_TO_CLOSE: 'option_buy_to_close',
  OPTION_SELL_TO_CLOSE: 'option_sell_to_close',
  OPTION_EXPIRED: 'option_expired',
  OPTION_ASSIGNED: 'option_assigned',
  OPTION_EXERCISED: 'option_exercised',
  DIVIDEND: 'dividend',
  CASH_DIVIDEND: 'cash_dividend',
  STOCK_DIVIDEND: 'stock_dividend',
  INTEREST_CREDIT: 'interest_credit',
  FEE: 'fee',
  COMMISSION: 'commission',
  TAX: 'tax',
  CASH_DEPOSIT: 'cash_deposit',
  CASH_WITHDRAWAL: 'cash_withdrawal',
  TRANSFER_IN: 'transfer_in',
  TRANSFER_OUT: 'transfer_out',
  JOURNAL_ENTRY: 'journal_entry',
  CORPORATE_ACTION: 'corporate_action',
  MANUAL_DEPOSIT: 'manual_deposit',
  MANUAL_WITHDRAWAL: 'manual_withdrawal',
  ACCOUNT_ADJUSTMENT: 'account_adjustment',
} as const

export type TransactionType = (typeof TransactionType)[keyof typeof TransactionType]

/**
 * Base cash ledger entry
 */
export interface CashLedgerEntryBase {
  transaction_type: TransactionType
  amount: number
  currency?: string
  transaction_date: string // ISO date string
  settlement_date?: string | null
  description?: string | null
  related_position_id?: string | null
  related_underlying_id?: string | null
  related_stock_trade_id?: string | null
  notes?: string | null
}

/**
 * Cash ledger entry for creation (API request)
 */
export interface CashLedgerEntryCreate extends CashLedgerEntryBase {
  portfolio_id?: string | null // Optional - defaults to user's default portfolio
  target_balance?: number | null
}

/**
 * Complete cash ledger entry (API response)
 */
export interface CashLedgerEntry extends CashLedgerEntryBase {
  id: string
  user_id: string
  created_at: string
  updated_at: string
  source_type?: string
  source_provider?: string | null
  source_broker_account_id?: string | null
}

/**
 * Reason codes surfaced when the computed cash balance is not reliable.
 * Kept as a union type so the UI can branch on specific states and still
 * fall back to a generic message on unknown values.
 */
export type BalanceReliabilityReason = 'no_cash_basis' | 'has_unresolved_write_failures'

export interface CashCurrencySummary {
  currency: string
  transaction_count: number
  ledger_cash_effect: number
  deposits: number
  withdrawals: number
  net_external_cash_flow: number
  option_received: number
  option_paid: number
  net_option_cash_flow: number
  stock_purchases: number
  stock_sales: number
  net_stock_investment: number
  dividends_received: number
  interest_received: number
  interest_paid: number
  standalone_fees_paid: number
  embedded_trade_fees: number
  fees_paid: number
  taxes_withheld: number
  transfers_in: number
  transfers_out: number
  other_adjustments: number
  sign_anomaly_count: number
}

export interface CashReconciliation {
  status: 'reconciled' | 'difference' | 'incomplete_history' | 'unavailable'
  currency: string | null
  starting_cash: number | null
  starting_cash_date: string | null
  tracked_cash_movement: number
  calculated_ending_cash: number | null
  broker_reported_cash: number | null
  difference: number | null
  tolerance: number
  history_start_date: string | null
  history_complete: boolean
  reason: string | null
}

/**
 * Cash ledger summary statistics
 */
export interface CashLedgerSummary {
  current_balance: number
  ledger_balance?: number | null
  total_deposits: number
  total_withdrawals: number
  total_premiums_received: number
  total_premiums_paid: number
  total_stock_purchases: number
  total_stock_sales: number
  net_premiums: number
  net_stock_trades: number
  net_external_cash_flow: number
  net_stock_investment: number
  total_dividends_received: number
  total_interest_received: number
  total_interest_paid: number
  total_fees_paid: number
  standalone_fees_paid: number
  fees_included_in_trade_cash: number
  total_taxes_withheld: number
  total_transfers_in: number
  total_transfers_out: number
  net_other_adjustments: number
  transaction_count: number
  primary_currency: string | null
  multiple_currencies: boolean
  balance_source: 'broker_snapshot' | 'imported_snapshot' | 'ledger' | 'unavailable'
  balance_as_of: string | null
  option_cash_flow_label: 'Option Premium Cash Flow' | 'Option Trade Cash Flow'
  option_cash_flow_net_of_fees: boolean
  sign_anomaly_count: number
  unclassified_transaction_count: number
  unclassified_cash_effect: number
  available_categories: string[]
  currency_summaries: CashCurrencySummary[]
  reconciliation: CashReconciliation | null
  is_balance_reliable: boolean
  balance_reliability_reason: BalanceReliabilityReason | null
  unresolved_write_failures: number
}
